// src/App.jsx
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppLayout from './components/layout/AppLayout';


// ── Pages existantes ──────────────────────────────────────────
import Landing         from './pages/Landing';
import Login           from './pages/Login';
import Dashboard       from './pages/Dashboard';
import Kanban          from './pages/Kanban';
import AODetail        from './pages/AODetail';
import CVConversion    from './pages/CVConversion';
import Notifications   from './pages/Notifications';
import CalendarPage    from './pages/CalendarPage';
import Admin           from './pages/Admin';
import SetPassword     from './pages/SetPassword';
import CandidateReview from './pages/Candidatereview';


// PipelineFormPage — remplace CandidateFormPage
import PipelineFormPage from './pages/PipelineFormPage';

// Référent Technique
import RTDashboard   from './pages/RTDashboard';
import RTReportEditor from './pages/RTReportEditor';


// Pipe candidats (nouveau)
import PipelineCandidats     from './pages/PipelineCandidats';
import PipelineCandidatDetail from './pages/PipelineCandidatDetail';


// Ressources ESN (Admin/RH/IA uniquement)
import Resources       from './pages/Resources';
import ResourceDetail  from './pages/ResourceDetail';
import ResourceFormPage from './pages/ResourceFormPage';


// Mon compte — tous les rôles
import MonCompte       from './pages/MonCompte';

// Besoins (Admin/Super Admin/IA)
import Besoins              from './pages/Besoins';
import BesoinDetail         from './pages/BesoinDetail';
import Positionnements      from './pages/Positionnements';
import PositionnementDetail from './pages/PositionnementDetail';
import Actions              from './pages/Actions';

// RH — Activité et Frais
import TimesheetList   from './pages/TimesheetList';
import TimesheetDetail from './pages/TimesheetDetail';
import Projects        from './pages/Projects';
import ProjectDetail   from './pages/ProjectDetail';
import SaleInvoices      from './pages/SaleInvoices';
import SaleInvoiceDetail from './pages/SaleInvoiceDetail';
import PurchaseInvoices   from './pages/PurchaseInvoices';
import PurchaseInvoiceDetail from './pages/PurchaseInvoiceDetail';
import NotesFrais      from './pages/NotesFrais';
import NotesFraisForm  from './pages/NotesFraisForm';
import Absences           from './pages/Absences';
import AbsenceForm        from './pages/AbsenceForm';
import AbsenceDetail      from './pages/AbsenceDetail';
import AbsencesImposees   from './pages/AbsencesImposees';
import AdminImport        from './pages/AdminImport';
import MesDocuments              from './pages/MesDocuments';
import CollaborateurDashboard    from './pages/CollaborateurDashboard';
import ArretsMaladie             from './pages/ArretsMaladie';
import ArretMaladieForm          from './pages/ArretMaladieForm';
import RhValidations             from './pages/RhValidations';
import RhDashboard               from './pages/RhDashboard';
import ComptableDashboard        from './pages/ComptableDashboard';
import RhDocuments               from './pages/RhDocuments';
import Equipe                    from './pages/Equipe';

// CRM Sociétés (Admin/IA uniquement)
import Companies       from './pages/Companies';
import CompanyDetail   from './pages/CompanyDetail';
import CompanyFormPage from './pages/CompanyFormPage';
import CRMContacts     from './pages/CRMContacts';
import CRMContactDetail    from './pages/CRMContactDetail';
import CRMContactFormPage  from './pages/CRMContactFormPage';


// ── Guards ────────────────────────────────────────────────────
const ADMIN_ROLES    = ['admin', 'super_admin', 'rh'];
const NO_TA_ROLES    = ['admin', 'super_admin', 'rh', 'business_manager', 'ingenieur_affaires', 'referent_technique', 'comptable'];
const CRM_ROLES      = ['admin', 'super_admin', 'business_manager', 'ingenieur_affaires'];
const RT_ROLES       = ['admin', 'super_admin', 'business_manager', 'ingenieur_affaires', 'referent_technique'];
const BESOINS_ROLES  = ['admin', 'super_admin', 'business_manager', 'ingenieur_affaires'];


function PrivateRoute({ children, allowedRoles = null }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', gap:12, color:'var(--text-muted)' }}>
      <div className="spinner" style={{ width:24, height:24 }}/><span>Chargement…</span>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  const role = (user.role || '').toLowerCase();
  if (allowedRoles && !allowedRoles.includes(role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function DashboardRedirect() {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  if (role === 'referent_technique') return <Navigate to="/rt-dashboard" replace />;
  if (role === 'collaborateur')      return <Navigate to="/collab-dashboard" replace />;
  if (role === 'rh')                 return <Navigate to="/rh-dashboard" replace />;
  if (role === 'comptable')          return <Navigate to="/comptable-dashboard" replace />;
  return <Dashboard />;
}

function TimesheetRedirect() {
  const now = new Date();
  return <Navigate to={`/timesheets/${now.getFullYear()}/${now.getMonth() + 1}`} replace />;
}


function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}


export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" toastOptions={{
          style: { fontFamily:'var(--font-sans)', fontSize:'0.875rem', borderRadius:'10px', boxShadow:'var(--shadow-md)', border:'1px solid var(--border)' },
          success: { iconTheme: { primary:'var(--success-600)', secondary:'white' } },
          error:   { iconTheme: { primary:'var(--danger-600)',  secondary:'white' } },
        }}/>


        <Routes>
          {/* Public */}
          <Route path="/login"           element={<PublicRoute><Login/></PublicRoute>}/>
          <Route path="/set-password"    element={<SetPassword/>}/>
          <Route path="/candidate-review" element={<CandidateReview/>}/>


          {/* Protected — layout commun */}
          <Route element={<PrivateRoute><AppLayout/></PrivateRoute>}>


            {/* Dashboard & AO */}
            <Route path="/dashboard"  element={<DashboardRedirect/>}/>
            <Route path="/kanban"     element={<Kanban/>}/>
            <Route path="/ao/:id"     element={<AODetail/>}/>
            <Route path="/cv"         element={<CVConversion/>}/>
            <Route path="/notifications" element={<Notifications/>}/>
            <Route path="/calendar"      element={<CalendarPage/>}/>


            {/* /candidates/* gardées mais cachées (utilisées par AODetail en interne) */}


            {/* Pipe candidats — tout le monde */}
            <Route path="/pipeline"          element={<PipelineCandidats/>}/>
            <Route path="/pipeline/new"      element={<PipelineFormPage/>}/>
            <Route path="/pipeline/:id/edit" element={<PipelineFormPage/>}/>
            <Route path="/pipeline/:id"      element={<PipelineCandidatDetail/>}/>


            {/* Ressources ESN — pas les TA */}
            <Route path="/resources"          element={<PrivateRoute allowedRoles={NO_TA_ROLES}><Resources/></PrivateRoute>}/>
            <Route path="/resources/new"      element={<PrivateRoute allowedRoles={NO_TA_ROLES}><ResourceFormPage/></PrivateRoute>}/>
            <Route path="/resources/:id/edit" element={<PrivateRoute allowedRoles={NO_TA_ROLES}><ResourceFormPage/></PrivateRoute>}/>
            <Route path="/resources/:id"      element={<PrivateRoute allowedRoles={NO_TA_ROLES}><ResourceDetail/></PrivateRoute>}/>


            {/* CRM — Admin + IA uniquement */}
            <Route path="/besoins"            element={<PrivateRoute allowedRoles={BESOINS_ROLES}><Besoins/></PrivateRoute>}/>
            <Route path="/besoins/:id"        element={<PrivateRoute allowedRoles={BESOINS_ROLES}><BesoinDetail/></PrivateRoute>}/>
            <Route path="/positionnements"    element={<PrivateRoute allowedRoles={['admin','super_admin','business_manager','ingenieur_affaires','rh','talent_acquisition']}><Positionnements/></PrivateRoute>}/>
            <Route path="/positionnements/:id" element={<PrivateRoute allowedRoles={['admin','super_admin','business_manager','ingenieur_affaires','rh','talent_acquisition']}><PositionnementDetail/></PrivateRoute>}/>

            <Route path="/companies"          element={<PrivateRoute allowedRoles={CRM_ROLES}><Companies/></PrivateRoute>}/>
            <Route path="/companies/new"      element={<PrivateRoute allowedRoles={CRM_ROLES}><CompanyFormPage/></PrivateRoute>}/>
            <Route path="/companies/:id/edit" element={<PrivateRoute allowedRoles={CRM_ROLES}><CompanyFormPage/></PrivateRoute>}/>
            <Route path="/companies/:id"      element={<PrivateRoute allowedRoles={CRM_ROLES}><CompanyDetail/></PrivateRoute>}/>
            <Route path="/crm-contacts"          element={<PrivateRoute allowedRoles={CRM_ROLES}><CRMContacts/></PrivateRoute>}/>
            <Route path="/crm-contacts/new"      element={<PrivateRoute allowedRoles={CRM_ROLES}><CRMContactFormPage/></PrivateRoute>}/>
            <Route path="/crm-contacts/:id/edit" element={<PrivateRoute allowedRoles={CRM_ROLES}><CRMContactFormPage/></PrivateRoute>}/>
            <Route path="/crm-contacts/:id"      element={<PrivateRoute allowedRoles={CRM_ROLES}><CRMContactDetail/></PrivateRoute>}/>
            <Route path="/actions"               element={<PrivateRoute allowedRoles={['admin','super_admin','business_manager','ingenieur_affaires','rh','talent_acquisition']}><Actions/></PrivateRoute>}/>


            {/* Activité & Frais — tout le monde */}
            <Route path="/timesheets"              element={<TimesheetRedirect/>}/>
            <Route path="/timesheets/history"      element={<TimesheetList/>}/>
            <Route path="/timesheets/:annee/:mois" element={<TimesheetDetail/>}/>
            <Route path="/projects"                element={<Projects/>}/>
            <Route path="/projects/:id"           element={<ProjectDetail/>}/>
            <Route path="/sale-invoices"          element={<SaleInvoices/>}/>
            <Route path="/sale-invoices/:id"      element={<SaleInvoiceDetail/>}/>
            <Route path="/purchase-invoices"      element={<PurchaseInvoices/>}/>
            <Route path="/purchase-invoices/:id"  element={<PurchaseInvoiceDetail/>}/>
            <Route path="/notes-frais"            element={<NotesFrais/>}/>
            <Route path="/notes-frais/nouveau"    element={<NotesFraisForm/>}/>
            <Route path="/notes-frais/:id"        element={<NotesFraisForm/>}/>
            <Route path="/absences"                    element={<Absences/>}/>
            <Route path="/absences/new"               element={<AbsenceForm/>}/>
            <Route path="/absences/:id"               element={<AbsenceDetail/>}/>
            <Route path="/absences/:id/edit"          element={<AbsenceForm/>}/>
            <Route path="/absences-imposees"          element={<AbsencesImposees/>}/>
            <Route path="/arrets-maladie"             element={<ArretsMaladie/>}/>
            <Route path="/arrets-maladie/nouveau"     element={<ArretMaladieForm/>}/>
            <Route path="/arrets-maladie/:id"         element={<ArretMaladieForm/>}/>
            <Route path="/rh-validations"             element={<RhValidations/>}/>
            <Route path="/rh-dashboard"               element={<RhDashboard/>}/>
            <Route path="/comptable-dashboard"        element={<ComptableDashboard/>}/>
            <Route path="/rh-documents"               element={<RhDocuments/>}/>

            {/* Référent Technique */}
            <Route path="/rt-dashboard"                    element={<PrivateRoute allowedRoles={RT_ROLES}><RTDashboard/></PrivateRoute>}/>
            <Route path="/rt-report/:aoId/:candidateId"    element={<PrivateRoute allowedRoles={RT_ROLES}><RTReportEditor/></PrivateRoute>}/>

            {/* Mon compte — tout le monde */}
            <Route path="/mon-compte"        element={<MonCompte/>}/>
            <Route path="/mes-documents"     element={<MesDocuments/>}/>
            <Route path="/collab-dashboard"  element={<CollaborateurDashboard/>}/>
            <Route path="/equipe"            element={<Equipe/>}/>

            {/* Admin */}
            <Route path="/admin" element={<PrivateRoute allowedRoles={ADMIN_ROLES}><Admin/></PrivateRoute>}/>
            <Route path="/admin/import" element={<PrivateRoute allowedRoles={['super_admin']}><AdminImport/></PrivateRoute>}/>
          </Route>


          {/* Landing & redirections */}
          <Route path="/"   element={<PublicRoute><Landing/></PublicRoute>}/>
          <Route path="*"   element={<Navigate to="/dashboard" replace/>}/>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}